import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { AccountsStats, OverdueSummary } from '@/lib/types';

/**
 * The accounts dashboard's two aggregate reads, in one hook.
 *
 * `GET /accounts/stats` answers the headline counts behind every stat card on
 * the dashboard — **all of them, in one read, whichever section is on screen**;
 * `GET /accounts/overdue-by-company` answers one row per company carrying an
 * overdue invoice. **Neither takes any parameters** — both query schemas are
 * empty and `.strict()` server-side, so a `page`, `perPage`, sort or filter is
 * a 422 naming the key.
 *
 * ⚠️ **MOUNTED ONCE, AT PAGE LEVEL, AND NEVER PER SECTION.** The stat cards are
 * per section since 2026-09-03, which makes "fetch the section's counts in the
 * section" look like the tidy arrangement — it is not. Both endpoints answer the
 * WHOLE dashboard whatever is on screen, so a per-section mount would re-fire
 * this pair on every chip press to read back the identical answer. The section
 * decides which counts are DRAWN, not which are read.
 * ⚠️ *SUPERSEDED: "against the shared 120-per-60s per-IP budget, and the person
 * it rate-limits is whoever opens the app next, not whoever was clicking."* The
 * budget is **12,000/60s** (corrected 2026-09-17), so chip presses will not
 * rate-limit anybody. The mount point is still page level, for the reason above
 * it: the response cannot differ per section.
 *
 * ## ⛔ Nothing here is derivable client-side, and no fallback may be added
 *
 * These endpoints exist because **no aggregate grouping documents or money by
 * company exists anywhere in this API**. Deriving the counts here means **one
 * request per company** — a per-row lookup behind a single headline figure,
 * which fails a company at a time and renders a total that is silently short.
 * So when a read is unavailable the answer is *no cards*, never *cards computed
 * another way*.
 * ⚠️ *SUPERSEDED: "against a **120-per-60s limit that is per-IP and shared
 * across the whole office**: the page would work for whoever opened it first
 * and rate-limit the next four."* It is **12,000/60s** (corrected 2026-09-17).
 * ⛔ **The refusal is unchanged — it is about the SHAPE and about a wrong total,
 * not about the ceiling.** A figure quietly missing the companies whose
 * requests failed is worse the more of them fit inside the budget.
 *
 * ## The three states this hook exists to keep apart
 *
 * In a system where a quiet week and a dead endpoint render the same row of
 * zeroes — `/deliverables` returned a `500` on every request for a day and
 * nobody noticed, because zero rows made the dead page render the documented
 * empty state — collapsing any two of these is the whole defect:
 *
 * | on screen | here |
 * | --- | --- |
 * | we asked, and there is nothing | `stats` populated with zeroes; `overdue` an EMPTY `Map` |
 * | we could not find out | `stats` / `overdue` **`null`**, with an error beside it |
 * | this API does not have it yet | `null`, with `…Missing` and **no** error |
 *
 * ⚠️ **So never `?? 0` a count and never `?? new Map()` an absent list** at a
 * call site. A zero rendered from a failure is a claim, and it is the flattering
 * kind: nobody reports a dashboard that says everything is settled.
 */
export interface AccountsStatsResult {
  /**
   * Every stat-card count, or `null` where they could not be read — still
   * loading, refused, or not on this API yet. **`null` is never zeroes.**
   *
   * ⚠️ **One member of the object is ITSELF nullable** — see
   * `AccountsStats.quotesPendingSignature` — so `stats !== null` does not mean
   * every figure is known, and a renderer must still branch per field.
   */
  stats: AccountsStats | null;
  /**
   * Overdue companies keyed by `companyId`, for an O(1) lookup from a table row
   * without scanning the list per render.
   *
   * ⚠️ **An EMPTY `Map` and `null` are different facts.** Empty means nobody is
   * overdue; `null` means the read did not answer. A row rendering a chip from
   * `overdue?.get(id)` gets `undefined` either way, which is correct for the
   * chip — but a page-level sentence about overdue accounts must branch on the
   * `Map` itself, or "no overdue invoices" is printed over an outage.
   */
  overdue: Map<number, OverdueSummary> | null;
  /**
   * Either read still in flight. A convenience over the two below — the reads
   * resolve independently, so `stats` can be populated while this is still
   * `true`. **Prefer `statsLoading` / `overdueLoading` for skeletons**, or one
   * surface shimmers while waiting for the other.
   */
  loading: boolean;
  /**
   * Every failure, in one sentence, or `null` when nothing failed.
   *
   * ⚠️ **A 404 does not appear here** — a route this API does not have yet is an
   * ordinary state, not a failure, and banners for it would follow this app into
   * every deploy that lands ahead of the server's.
   *
   * ⚠️ **The two messages are JOINED, never coalesced.** `statsError ??
   * overdueError` would drop a live failure whenever both are set — the silent
   * `??` drop this codebase records four instances of. Both sides can be true at
   * once, so both are rendered.
   */
  error: string | null;

  /** `stats` has not answered yet. */
  statsLoading: boolean;
  /** `overdue` has not answered yet. */
  overdueLoading: boolean;
  /** Why the counts could not be read. `null` on a 404 — see {@link error}. */
  statsError: string | null;
  /** Why the overdue list could not be read. `null` on a 404. */
  overdueError: string | null;
  /**
   * `GET /accounts/stats` answered **404**: not set up on this API yet.
   *
   * Render nothing — no cards and no failure block, the treatment
   * `/integrations` gives an unwired entity. It is **not** an error, and it is
   * **not** a reason to derive the counts another way.
   */
  statsMissing: boolean;
  /** `GET /accounts/overdue-by-company` answered 404. See {@link statsMissing}. */
  overdueMissing: boolean;
}

/**
 * One read's outcome, held as a unit so the four facts about it are written in
 * one `setState` and cannot describe two different moments.
 */
interface ReadState<T> {
  value: T | null;
  loading: boolean;
  error: string | null;
  missing: boolean;
}

const PENDING: ReadState<never> = { value: null, loading: true, error: null, missing: false };

/**
 * ⚠️ **A 404 is separated FIRST, before anything else is read off the error.**
 * This app deploys separately from the API and can be newer than it, so a route
 * that does not exist yet is an ordinary answer — the same ruling
 * `accountsDashboardSheet`, `useXeroBrandingThemes` and the DocuSign card all
 * make. Treating it as a failure puts a red banner on a correctly-working page
 * for the whole window between two deploys.
 *
 * Everything else keeps the SERVER'S OWN SENTENCE where there is one: a 403
 * naming what the reader may not do is more use than a house sentence, and this
 * repo has already replaced one diagnosis with a tidier one that had no next
 * step in it.
 */
function failureOf<T>(caught: unknown, fallback: string): ReadState<T> {
  if (caught instanceof ApiError && caught.status === 404) {
    return { value: null, loading: false, error: null, missing: true };
  }
  return {
    value: null,
    loading: false,
    error: caught instanceof Error && caught.message !== '' ? caught.message : fallback,
    missing: false,
  };
}

/**
 * Keyed by `companyId` so a table row can ask about itself without scanning.
 *
 * The contract is one row per company; a duplicate would be last-wins here, and
 * silently — that is a server-side invariant this cannot check, and inventing a
 * merge would produce a total belonging to no response.
 */
function byCompany(rows: readonly OverdueSummary[]): Map<number, OverdueSummary> {
  const map = new Map<number, OverdueSummary>();
  for (const row of rows) map.set(row.companyId, row);
  return map;
}

/**
 * ⚠️ **Two chains under one controller, and deliberately NOT `Promise.all`.**
 * `Promise.all` rejects wholesale on the first failure, so an overdue read that
 * 500s would take the stat cards down with it — and a missing overdue chip is
 * invisible, which is the worse direction of the two. Each read lands on its own
 * slice, so one failing leaves the other rendering exactly as it would have.
 *
 * Takes no arguments and reads nothing from the outside: both endpoints are
 * parameterless, so there is nothing for an effect to depend on and the pair is
 * fetched once per mount.
 */
export function useAccountsStats(): AccountsStatsResult {
  const [stats, setStats] = useState<ReadState<AccountsStats>>(PENDING);
  const [overdue, setOverdue] = useState<ReadState<Map<number, OverdueSummary>>>(PENDING);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * ⚠️ `signal.aborted` is guarded in BOTH arms, not only the `.catch`. An
     * abort rejects the in-flight request, so the failure path is the one an
     * unmount actually takes — and a `setState` after unmount on a page the
     * reader has left is at best noise and at worst a stale slice restored over
     * a fresh one when React reuses the component.
     */
    api
      .accountsStats(signal)
      .then((answer) => {
        if (signal.aborted) return;
        setStats({ value: answer.data, loading: false, error: null, missing: false });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setStats(failureOf(caught, 'The accounts figures could not be read.'));
      });

    api
      .accountsOverdueByCompany(signal)
      .then((answer) => {
        if (signal.aborted) return;
        setOverdue({
          value: byCompany(answer.data),
          loading: false,
          error: null,
          missing: false,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setOverdue(failureOf(caught, 'The overdue totals could not be read.'));
      });

    return () => controller.abort();
  }, []);

  /*
   * ⚠️ JOINED, never `statsError ?? overdueError`. Both halves can be true at
   * once — two independent reads against one server having a bad minute is the
   * ordinary way that happens — and a coalesce would report one failure and
   * silently swallow the other, on a page whose whole job is to say what is
   * outstanding.
   */
  const messages = [stats.error, overdue.error].filter((m): m is string => m !== null);

  return {
    stats: stats.value,
    overdue: overdue.value,
    loading: stats.loading || overdue.loading,
    error: messages.length === 0 ? null : messages.join(' '),
    statsLoading: stats.loading,
    overdueLoading: overdue.loading,
    statsError: stats.error,
    overdueError: overdue.error,
    statsMissing: stats.missing,
    overdueMissing: overdue.missing,
  };
}
